/** @jsx isolateComponent */
import { css } from '@emotion/core';
import {
  Colors,
  Heading,
  HeadingLevel,
  Img,
  Link,
  Typography,
} from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { useUniversalNavContext } from '../../../hooks/useUniversalNavContext';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';
import { ArrowLinkIcon } from '../ArrowLinkIcon';
import { PRODUCT_LINKS } from '../constants';

type Props = {
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

const Styles = labelStyles('StaticProductLeft', {
  root: css({
    display: 'flex',
    columnGap: '30px',
    alignItems: 'center',
  }),
  image: css({
    width: 250,
    height: 230,
  }),
  textContainer: css({
    display: 'flex',
    flexDirection: 'column',
  }),
  heading: css({
    ...Typography.pageHeader9,
    display: 'flex',
    flexDirection: 'column',
    color: Colors.earth,
    lineHeight: '24px',
  }),
  subheading: css({
    ...Typography.smallPrint1,
    color: Colors.neutral700,
    display: 'block',
    marginTop: 8,
  }),
  divider: css({
    margin: '16px 0',
    height: 1,
    backgroundColor: Colors.neutral300,
    width: 380,
  }),
  linksContainer: css({
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    gap: '9px 50px',
    '@media screen and (min-width: 1080px)': {
      gap: '9px 60px',
    },
  }),
  linkContainer: css({
    textDecoration: 'none',
    display: 'flex',
    flexDirection: 'column',
    '&:hover [data-tag="link-heading"]': {
      color: Colors.zapier,
      textDecoration: 'none',
    },
    '&:hover [data-tag="link-description"]': {
      textDecoration: 'underline',
    },
    '&:hover [data-tag="arrow-icon-wrapper"]': {
      marginLeft: 4,
      opacity: 1,
    },
    '&:focus': {
      outline: `1px solid ${Colors.blue}`,
    },
  }),
  linkHeading: css({
    ...Typography.paragraph3Semibold,
    textDecoration: 'none',
    color: Colors.earth,
    lineHeight: '30px',
  }),
  linkDescription: css({
    ...Typography.smallPrint3,
    textDecoration: 'none',
    color: Colors.neutral700,
    lineHeight: '20px',
  }),
});

type ZapLinks = {
  description: string;
  href: string;
  text: string;
}[];

const zapLinks: ZapLinks = [
  PRODUCT_LINKS.howItWorks,
  PRODUCT_LINKS.security,
  PRODUCT_LINKS.features,
  PRODUCT_LINKS.appIntegrations,
];

/**
 * @private
 * Wrapper for the static content inside the Product tab's left side
 */
const StaticProductLeft = ({ onCloseMenu }: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();
  const handleEvent = (text: string, eventType: string) => {
    emitUserInteractionEvent({
      event_action: eventType,
      option_selected: `Product tab Left: ${text}`,
    });
    if (eventType === 'click' && onCloseMenu) onCloseMenu();
  };
  return (
    <div css={Styles.root}>
      <Img
        alt="A simplified user interface design representing a portion of Zapier's platform. There's a vertical menu of platform options. At the top is a dominant orange button with text inside that reads 'Create Zap.' Beneath the button is a stack of menu icons to represent dashboard, Zaps, and Transfers."
        css={Styles.image}
        src="https://cdn.zapier.com/storage/photos/39bccc5dc95c6dd96978b7b5635467a8.png"
      />
      <div css={Styles.textContainer}>
        <header>
          <HeadingLevel>
            <Heading css={Styles.heading}>Zapier Automation Platform</Heading>
          </HeadingLevel>
          <span css={Styles.subheading}>
            No-code automation across 5,000+ apps
          </span>
        </header>

        <hr css={Styles.divider} />

        <ul css={Styles.linksContainer}>
          {zapLinks.map((link) => (
            <li key={link.text}>
              <Link
                color={null}
                css={Styles.linkContainer}
                href={getFullyQualifiedURL(link.href)}
                onClick={() => handleEvent(link.text, 'click')}
                onMouseEnter={() => handleEvent(link.text, 'hover')}
              >
                <span css={Styles.linkHeading} data-tag="link-heading">
                  {link.text}
                </span>
                <span css={Styles.linkDescription} data-tag="link-description">
                  {link.description}
                  <ArrowLinkIcon />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default StaticProductLeft;
